import { TreeView } from '@mui/lab';
import { Box, InputAdornment, OutlinedInput, styled, Typography } from '@mui/material';
import { Icon, IconToggle } from 'components';
import { theme } from 'config';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuthStore, RoutingStore } from 'stores';
import { TreeDataItem } from 'types';
import { hierarchyIconIds, Tree } from '../../stores/tree';
import { ContentTreeItem } from './contentTreeItem';
import { TreeContext } from './treeViewContainer';


export interface IContentTreeViewProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}

export const ContentTreeView = inject('authStore', 'routingStore')(observer((props: IContentTreeViewProps): JSX.Element => {
  const { t } = useTranslation();
  const { authStore } = props;
  const tree: Tree = React.useContext(TreeContext);
  const [searchString, setSearchString] = React.useState<string>('');

  const filteredData: TreeDataItem[] = React.useMemo(() => {
    if (!searchString || !searchString.length) return tree.getRootData
    return tree.getRootData.filter((c) =>
      c.label.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [tree.data, searchString, tree.hierarchy]);

  return (
    <StyledTreeView
      sx                  = {{ width: 'inherit' }}
      defaultCollapseIcon = {<Icon size='size-12' iconId='arrow-down' />}
      defaultExpandIcon   = {<Icon size='size-12' iconId='arrow-right' />}
    >
      <Box mt={'2.7rem'}>
        <Box 
          display        = "flex"
          flexDirection  = "row"
          justifyContent = "space-between"
          alignItems     = "start"
        >
          <OutlinedInput
            sx             = {{ ml: 2 }}
            value          = {searchString}
            placeholder    = {t(tree.hierarchy[0])}
            onChange       = {(e) => setSearchString(e.target.value)}
            startAdornment = {
              <InputAdornment position = 'start'>
                <Icon size='size-18' iconId   = {hierarchyIconIds[tree.hierarchy[0]]} />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position = 'end'>
                <Icon size='size-18' iconId='search' />
              </InputAdornment>
            }
          />
            <Box display='flex' flexDirection='row' gap={2}>
              <IconToggle 
                iconOn         = {<Icon size='size-16' iconId='view'/>}
                iconOff        = {<Icon size='size-16' iconId='view-off'/>}
                selected       = {tree.show_empty_tree}
                onChange       = {tree.toggleShowEmptyTree}
                tooltipIconOff = {t("show_empty_branches")}
                tooltipIconOn  = {t('hide_empty_branches')}
              />
              <IconToggle 
                iconOn         = {<Icon size='size-16' iconId='lock'/>}
                iconOff        = {<Icon size='size-16' iconId='unlock'/>}
                selected       = {tree.show_fake_data}
                onChange       = {tree.toggleShowFakeData}
                tooltipIconOff = {t("hide_real_data")}
                tooltipIconOn  = {t("show_real_data")}
              />
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridAutoColumns: '1fr', mt: 2, mb: 1 }}>
          <Box
            sx={{
              pl:             '8rem',
              gridRow:        '1',
              gridColumn:     'span 2',
              justifyContent: 'center',
            }}
          >
            <Typography variant='h6' fontSize={theme.typography.fontSize}>
              {t('statistic_data_type')}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: 'span 2', gridRow: '1', pl: 4 }}>
            <Typography variant='h6' fontSize={theme.typography.fontSize}>
              {t('statistic_last_updated')}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: 'span 3', gridRow: '1', pl: 2 }}>
            <Typography variant='h6' fontSize={theme.typography.fontSize}>
              {t('statistic_progress')}
            </Typography>
          </Box>
          <Box
            sx={{
              pl:             4,
              gridRow:        '1',
              display:        'flex',
              gridColumn:     'span 1',
              justifyContent: 'center',
            }}
          >

            <Typography variant='h6' fontSize={theme.typography.fontSize}>
              {t('statistic_filter')}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: '100%' }}>
        {filteredData.map((d: TreeDataItem) => (
          <StyledContentTreeItem
            key     = {d.id}
            depth   = {0}
            item    = {d}
            checked = {d.checked}
          />
        ))}
      </Box>
    </StyledTreeView>
  );
}));

const StyledTreeView = styled(TreeView)`
  margin-left: 2rem;

  .MuiTreeItem-content {
    height:           4.2rem;
    margin-bottom:    0.5rem;
    padding:          0 1.5rem;
    background-color: white;

    .MuiTreeItem-iconContainer {
      width: 1rem;
    }
  }

  .MuiCollapse-root {
    .MuiTreeItem-group {
      margin-left: 0;
      background:  transparent;
    }
  }
`;

const StyledContentTreeItem = styled(ContentTreeItem)`
  .MuiTreeItem-content {
    padding: 2rem;

    .MuiTreeItem-iconContainer {
      width: fit-content;
    }
  }
`;
